import { Button } from '@/components/ui/button'
import { useEffect, useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
const HEALTH_CHECK_INTERVAL = 30000 // Check every 30 seconds
const INITIAL_CHECK_DELAY = 2000 // Wait 2 seconds before first check

export function ApiHealthCheck({ children }: { children: React.ReactNode }) {
  const [isApiDown, setIsApiDown] = useState(false)
  const [isChecking, setIsChecking] = useState(false)

  const checkApiHealth = async () => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

      const response = await fetch(`${API_URL}/healthcheck`, {
        signal: controller.signal,
        method: 'GET',
        cache: 'no-store',
      })

      clearTimeout(timeoutId)

      if (response.status === 200) {
        setIsApiDown(false)
      } else {
        setIsApiDown(true)
      }
    } catch (error) {
      console.error('API health check failed:', error)
      setIsApiDown(true)
    }
  }

  const handleRetry = async () => {
    setIsChecking(true)
    await checkApiHealth()
    setIsChecking(false)
  }

  useEffect(() => {
    // Initial check after a short delay
    const initialTimeout = setTimeout(() => {
      checkApiHealth()
    }, INITIAL_CHECK_DELAY)

    // Set up recurring health checks
    const interval = setInterval(() => {
      checkApiHealth()
    }, HEALTH_CHECK_INTERVAL)

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [])

  if (isApiDown) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-md w-full mx-4 bg-white rounded-lg shadow-2xl p-6 border border-orange-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-orange-600"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              We're Having Connection Issues
            </h1>
          </div>
          <div className="space-y-3 mb-6">
            <p className="text-gray-700">
              We're having trouble connecting to our servers right now. This
              could be due to:
            </p>
            <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-2">
              <li>Temporary server maintenance</li>
              <li>Your internet connection</li>
              <li>A brief service interruption</li>
            </ul>
            <p className="text-gray-700 text-sm">
              Don't worry - we're working on it! Please try again in a moment.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleRetry}
              disabled={isChecking}
              className="flex-1"
            >
              {isChecking ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Checking...
                </>
              ) : (
                'Try Again'
              )}
            </Button>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              className="flex-1"
            >
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
